/** Sélection ou création de l'élection, pour un rôle donné. */

import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ref } from 'lit/directives/ref.js';
import { DEFAULT_RULES, type ElectionConfig } from '../../core/index.js';
import { StoreElement } from '../base.js';
import { chargerExemple } from '../demo.js';
import { dateLongue, nomFichier, telecharger } from '../format.js';
import { icone } from '../icons.js';
import { valeurDe } from '../md.js';
import type { Role } from '../store.js';

@customElement('view-choix')
export class ViewChoix extends StoreElement {
  /**
   * Rôle demandé. Nommé `espace` et non `role` : la propriété `role` d'un
   * élément HTML porte le rôle ARIA, la détourner nuirait à l'accessibilité.
   */
  @property({ type: String }) espace: Role = 'bureau';

  @state() private nom = '';
  @state() private classe = '';
  @state() private etablissement = '';
  @state() private dateISO = new Date().toISOString().slice(0, 10);
  @state() private occupe = false;
  @state() private message = '';
  @state() private messageFictive = '';
  @state() private chargementFictive = false;
  private fichierSauvegarde: HTMLInputElement | null = null;

  private readonly refFichier = (element?: Element): void => {
    this.fichierSauvegarde = (element as HTMLInputElement | undefined) ?? null;
  };

  /**
   * Restauration d'une sauvegarde, accessible **même sans aucune élection sur
   * l'appareil** : c'est précisément le cas après un effacement des données du
   * navigateur, ou sur une machine neuve.
   */
  private async restaurer(evenement: Event): Promise<void> {
    const champ = evenement.target as HTMLInputElement;
    const fichier = champ.files?.[0];
    if (fichier === undefined) return;
    try {
      const texte = await fichier.text();
      const electionId = await this.store.importerSauvegarde(texte);
      this.message = 'Sauvegarde restaurée et vérifiée.';
      location.hash = `#/bureau/${electionId}`;
    } catch (erreur) {
      this.message = erreur instanceof Error ? erreur.message : 'Restauration impossible.';
    }
    champ.value = '';
  }

  private async creer(): Promise<void> {
    if (this.nom.trim().length === 0 || this.classe.trim().length === 0) return;
    this.occupe = true;
    const config: ElectionConfig = {
      nom: this.nom.trim(),
      classe: this.classe.trim(),
      etablissement: this.etablissement.trim(),
      dateISO: this.dateISO,
      rules: DEFAULT_RULES,
    };
    const id = await this.store.create(config);
    this.occupe = false;
    location.hash = `#/bureau/${id}`;
  }

  /** Monte une élection de démonstration complète, prête à voter. */
  private async exemple(): Promise<void> {
    this.occupe = true;
    const id = await chargerExemple();
    this.occupe = false;
    location.hash = `#/bureau/${id}`;
  }

  /**
   * Charge l'élection **fictive** embarquée : un scrutin joué de bout en bout,
   * livré sous forme de sauvegarde JSON et vérifié comme n'importe quelle
   * restauration. Elle arrive déverrouillée : sa planche de codes est publique
   * par construction.
   */
  private async chargerFictive(): Promise<void> {
    this.chargementFictive = true;
    this.messageFictive = '';
    try {
      const adresse = new URL('./exemples/election-fictive.json', location.href);
      const reponse = await fetch(adresse, { cache: 'no-cache' });
      if (!reponse.ok) throw new Error(`fichier indisponible (${reponse.status})`);
      const electionId = await this.store.importerSauvegarde(await reponse.text(), { deverrouiller: true });
      location.hash = `#/bureau/${electionId}`;
    } catch (erreur) {
      this.messageFictive =
        erreur instanceof Error ? `Chargement impossible : ${erreur.message}` : 'Chargement impossible.';
    } finally {
      this.chargementFictive = false;
    }
  }

  private exporter(resumeId: string): void {
    // Export de sauvegarde : le journal complet, pour archivage ou reprise.
    const entrees: unknown[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const cle = localStorage.key(i);
      if (cle === null || !cle.startsWith(`election.nsi.xyz/v1/${resumeId}/`)) continue;
      try {
        const parse = JSON.parse(localStorage.getItem(cle) ?? '[]') as unknown;
        if (Array.isArray(parse)) entrees.push(...parse);
      } catch {
        // ignoré
      }
    }
    telecharger(nomFichier(`journal-${resumeId}`, 'json'), JSON.stringify(entrees, null, 2));
  }

  override render() {
    const elections = this.store.summaries();
    const estBureau = this.espace === 'bureau';

    return html`
      <div class="page page--narrow">
        <div class="row row--between">
          <h1 class="t-headline">${estBureau ? 'Choisir une élection' : this.espace === 'isoloir' ? 'Isoloir' : 'Affichage'}</h1>
          <md-text-button @click=${() => (location.hash = '#/')}>Accueil</md-text-button>
        </div>

        ${elections.length === 0
          ? html`<section class="card card--flat">
              <p class="t-body">Aucune élection n’est ouverte sur cet appareil.</p>
              ${!estBureau
                ? html`<p class="t-body t-muted">
                    Le vote se déroule sur <strong>l’appareil de l’enseignant</strong>, avec le code imprimé sur votre
                    papier : présentez-vous à lui. Ouvrir cette page sur votre téléphone personnel ne suffit pas — le
                    vote depuis un appareil distinct n’est pas encore disponible.
                  </p>`
                : nothing}
            </section>`
          : html`<section class="card">
              <div class="card__head">
                <span class="chip-icon chip-icon--ok" aria-hidden="true">${icone('check', 22)}</span>
                <h2 class="t-title">Ouvrir une élection existante</h2>
              </div>
              <ul class="list">
                ${elections.map(
                  (resume) => html`
                    <li class="list__item">
                      <span class="grow">
                        <strong>${resume.nom}</strong><br />
                        <span class="t-muted">${resume.classe} · ${resume.etablissement || 'établissement non précisé'} · ${dateLongue(resume.dateISO)}</span>
                      </span>
                      <md-text-button @click=${() => this.exporter(resume.id)} title="Exporter le journal (sauvegarde)"
                        >Exporter</md-text-button
                      >
                      <md-filled-button @click=${() => (location.hash = `#/${this.espace}/${resume.id}`)}
                        >Ouvrir</md-filled-button
                      >
                    </li>
                  `,
                )}
              </ul>
            </section>`}

        ${estBureau
          ? html`<section class="card">
              <div class="card__head">
                <span class="chip-icon chip-icon--doux" aria-hidden="true">${icone('telecharger', 22)}</span>
                <h2 class="t-title">Restaurer une sauvegarde</h2>
              </div>
              <p class="t-body t-muted">
                Après un effacement des données du navigateur, ou sur un autre appareil : le fichier de sauvegarde
                exporté depuis le bureau contient le journal complet et les codes de vote. Il est vérifié avant
                restauration.
              </p>
              ${this.message !== '' ? html`<p class="t-body t-success">${this.message}</p>` : nothing}
              <div class="row">
                <md-outlined-button @click=${() => this.fichierSauvegarde?.click()}
                  >Choisir un fichier de sauvegarde</md-outlined-button
                >
                <input
                  type="file"
                  accept=".json,application/json"
                  class="champ-fichier"
                  aria-label="Fichier de sauvegarde à restaurer"
                  ${ref(this.refFichier)}
                  @change=${(evenement: Event) => void this.restaurer(evenement)}
                />
              </div>
            </section>`
          : nothing}

        ${estBureau
          ? html`<section class="card">
              <div class="card__head">
                <span class="chip-icon" aria-hidden="true">${icone('plus', 22)}</span>
                <h2 class="t-title">Créer une élection</h2>
              </div>
              <md-outlined-text-field
                label="Nom de l’élection"
                .value=${this.nom}
                @input=${(evenement: Event) => (this.nom = valeurDe(evenement))}
              ></md-outlined-text-field>
              <div class="grid-2">
                <md-outlined-text-field
                  label="Classe"
                  .value=${this.classe}
                  @input=${(evenement: Event) => (this.classe = valeurDe(evenement))}
                ></md-outlined-text-field>
                <md-outlined-text-field
                  label="Établissement"
                  .value=${this.etablissement}
                  @input=${(evenement: Event) => (this.etablissement = valeurDe(evenement))}
                ></md-outlined-text-field>
              </div>
              <div class="field">
                <label for="date-election">Date de l’élection</label>
                <input
                  id="date-election"
                  type="date"
                  .value=${this.dateISO}
                  @input=${(evenement: Event) => (this.dateISO = valeurDe(evenement))}
                />
              </div>
              <p class="t-body t-muted">
                Deux délégués titulaires, deux suppléants, deux voix par électeur au premier tour. Les données sont
                supprimées sept jours après l’élection, ou sept jours après la création si elle n’a pas lieu.
              </p>
              <div class="row row--end">
                <md-text-button ?disabled=${this.occupe} @click=${() => void this.exemple()}
                  >Essai rapide avec un exemple</md-text-button
                >
                <md-filled-button
                  ?disabled=${this.occupe || this.nom.trim() === '' || this.classe.trim() === ''}
                  @click=${() => void this.creer()}
                  >Créer l’élection</md-filled-button
                >
              </div>
            </section>`
          : nothing}

        ${estBureau
          ? html`<section class="card card--accent">
              <div class="card__head">
                <span class="chip-icon" aria-hidden="true">${icone('liste', 22)}</span>
                <div class="stack stack--tight grow">
                  <h2 class="t-title">Élection fictive</h2>
                  <p class="t-body">
                    Un scrutin joué de bout en bout, chargé depuis un fichier JSON : 12 élèves, 3 binômes, deux tours,
                    un bulletin blanc, et les résultats proclamés. Journal vérifié, planche de codes incluse.
                  </p>
                </div>
              </div>
              <div class="bloc">
                <p class="t-label">À quoi ça sert</p>
                <p class="t-body">
                  Voir un bureau complet, un dépouillement réel et un procès-verbal rempli, sans rien préparer.
                  L’élection apparaît ensuite dans la liste ci-dessus, comme n’importe quelle autre.
                </p>
                ${this.messageFictive !== '' ? html`<p class="t-body t-error">${this.messageFictive}</p>` : nothing}
              </div>
              <div class="bloc bloc--actions">
                <p class="t-label">Action</p>
                <md-filled-tonal-button ?disabled=${this.chargementFictive} @click=${() => void this.chargerFictive()}
                  >${this.chargementFictive ? 'Chargement…' : 'Charger l’élection fictive'}</md-filled-tonal-button
                >
              </div>
            </section>`
          : nothing}
      </div>
    `;
  }
}
